/**
 * Project-membership gating for member-only write actions.
 *
 * The server enforces "members only, leadership/admin bypass" on Phase 0 generate/upload/link/
 * delete, project chat writes, branded-docs regenerate, meetings, weekly reports and business-
 * context writes. GET /api/projects/{id} reports `viewer_is_member` (team membership only — no
 * role bypass), so the bypass is applied here. The UI gate is UX only: the server is the enforcer
 * and answers 403 NOT_PROJECT_MEMBER (or 404 for some routes) regardless of what we render.
 *
 * Deliberately has NO dependency on `@/lib/api`: many component tests replace that module with a
 * factory exposing only `useApiClient`, and api.ts itself imports the constants below for its
 * interceptor.
 */
import axios from "axios";
import { toast } from "sonner";

import type { Project } from "@/types";

/** Backend error code for a member-only write attempted by a user who is not on the team. */
export const NOT_PROJECT_MEMBER = "NOT_PROJECT_MEMBER";

/**
 * Sonner toast id for NOT_PROJECT_MEMBER. Shared by the axios interceptor (lib/api.ts) and the
 * mutation `onError` handlers so the same 403 never stacks two identical toasts.
 */
export const NOT_PROJECT_MEMBER_TOAST_ID = "not-project-member";

export const NOT_PROJECT_MEMBER_FALLBACK =
  "Only members of this project can make this change.";

/** Roles that act on any project without being on its team. */
const MEMBERSHIP_BYPASS_ROLES: readonly string[] = ["leadership", "admin"];

/** Roles that never see the Oscar Intakes surface (nav item, route, tab switcher). */
const INTAKES_HIDDEN_ROLES: readonly string[] = ["pm"];

/** False for PMs: the Intakes tab is hidden and /intakes redirects to /projects. */
export function canViewIntakes(role: string | null | undefined): boolean {
  return !INTAKES_HIDDEN_ROLES.includes(role ?? "");
}

export function isMembershipBypass(role: string | null | undefined): boolean {
  return MEMBERSHIP_BYPASS_ROLES.includes(role ?? "");
}

/**
 * True when `role` is allowed for the action AND the viewer is a member (or bypasses membership).
 * `project` undefined/null means it is still loading → false, so member-only controls never flash
 * on for a non-member.
 */
export function canActAsMember(
  role: string | null | undefined,
  allowedRoles: readonly string[],
  project: Pick<Project, "viewer_is_member"> | null | undefined,
): boolean {
  if (!allowedRoles.includes(role ?? "")) return false;
  if (!project) return false;
  return isMembershipBypass(role) || project.viewer_is_member === true;
}

interface ApiErrorBody {
  code?: unknown;
  message?: unknown;
}

function errorBody(err: unknown): ApiErrorBody | undefined {
  if (!axios.isAxiosError(err)) return undefined;
  const data = err.response?.data as ApiErrorBody | undefined;
  return data && typeof data === "object" ? data : undefined;
}

export function isNotProjectMemberError(err: unknown): boolean {
  return errorBody(err)?.code === NOT_PROJECT_MEMBER;
}

/**
 * If `err` is a 403 NOT_PROJECT_MEMBER, toast the server's message (deduplicated by id) and return
 * true so the caller can skip its generic failure toast.
 */
export function toastIfNotProjectMember(err: unknown): boolean {
  if (!isNotProjectMemberError(err)) return false;
  const message = errorBody(err)?.message;
  toast.error(
    typeof message === "string" && message
      ? message
      : NOT_PROJECT_MEMBER_FALLBACK,
    { id: NOT_PROJECT_MEMBER_TOAST_ID },
  );
  return true;
}
